import { useQuery } from '@tanstack/react-query'
import { Command } from 'cmdk'
import type { JSX } from 'react'
import { Link } from '../design-system/link.tsx'
import { DISPLAYS_QUERY_KEY, fetchDisplays } from '../lib/display-catalogue.ts'
import type { SearchPage } from '../lib/page-search.ts'
import { QUIET_QUERY_META } from '../lib/query-activity.ts'
import {
  displaySearchHref,
  searchDisplayIntent,
  type SearchDisplayIntent
} from '../lib/search-display-intent.ts'

interface DisplayActionProps {
  query: string
  entry: SearchPage
  pages: readonly SearchPage[]
  mode: 'palette' | 'keyword' | 'suggestion'
  onNavigate: (path: string) => void
}

/** Offer a TV action only when this viewer may open the editor. */
export function SearchDisplayAction(
  props: DisplayActionProps
): JSX.Element | null {
  const intent = searchDisplayIntent(props.query)
  const editor = props.pages.find(
    entry => entry.page.id === 'platform-displays'
  )?.page
  return intent && editor ? (
    <LoadedDisplayAction {...props} intent={intent} editor={editor.path} />
  ) : null
}

function LoadedDisplayAction({
  intent,
  editor,
  entry,
  mode,
  onNavigate
}: DisplayActionProps & {
  intent: SearchDisplayIntent
  editor: string
}): JSX.Element | null {
  const query = useQuery({
    queryKey: DISPLAYS_QUERY_KEY,
    queryFn: fetchDisplays,
    meta: QUIET_QUERY_META,
    retry: false
  })
  const data = query.data
  if (!data?.pages.some(page => page.path === entry.page.path)) return null
  const href = displaySearchHref(editor, entry.page.path, intent, [
    ...data.displays,
    ...data.screens
  ])
  const testId =
    mode === 'palette'
      ? `page-palette-${entry.page.id}-display`
      : `search-page-${mode === 'keyword' ? 'result' : 'jev'}-${entry.page.id}-display`
  const text = (
    <>
      Add {entry.page.title} to a TV
      <span className="text-muted-foreground text-xs">
        Open the playlist editor. Review the addition and save it.
      </span>
    </>
  )
  const className =
    'hover:bg-accent flex cursor-pointer flex-col gap-1 p-4 text-sm'
  if (mode === 'palette')
    return (
      <Command.Item
        data-testid={testId}
        value={`display\n${entry.page.id}`}
        className={className}
        onSelect={() => onNavigate(href)}
      >
        {text}
      </Command.Item>
    )
  const link = (
    <Link
      data-testid={testId}
      href={href}
      onNavigate={() => onNavigate(href)}
      className={className}
    >
      {text}
    </Link>
  )
  return mode === 'keyword' ? <li>{link}</li> : link
}
